/**
 * PlaceholderHighlight TipTap Extension
 *
 * Requirements Summary:
 * - Highlights [Placeholder Name] tokens with a colored badge style
 * - Uses ProseMirror decorations so the document model is untouched
 * - Works with all bracketed placeholder patterns, including legacy tokens in older policies.
 * - Purely visual — does not change the stored HTML
 *
 * Related files:
 * - /utils/policies/policyPlaceholders.ts - Placeholder definitions
 * - /components/form/fields/RichTextEditor.tsx - Editor that uses this extension
 */

import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const PLACEHOLDER_REGEX = /\[[A-Z][A-Za-z &-]+\]/g;

const placeholderHighlightKey = new PluginKey('placeholderHighlight');

const PlaceholderHighlight = Extension.create({
  name: 'placeholderHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: placeholderHighlightKey,
        state: {
          init(_, { doc }) {
            return buildDecorations(doc);
          },
          apply(tr, oldSet) {
            if (tr.docChanged) {
              return buildDecorations(tr.doc);
            }
            return oldSet;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

function buildDecorations(doc: any): DecorationSet {
  const decorations: Decoration[] = [];

  doc.descendants((node: any, pos: number) => {
    if (!node.isText || !node.text) return;

    let match;
    PLACEHOLDER_REGEX.lastIndex = 0;
    while ((match = PLACEHOLDER_REGEX.exec(node.text)) !== null) {
      const from = pos + match.index;
      const to = from + match[0].length;
      decorations.push(
        Decoration.inline(from, to, {
          class: 'policy-placeholder-token',
          'data-placeholder': match[0],
        })
      );
    }
  });

  return DecorationSet.create(doc, decorations);
}

export default PlaceholderHighlight;
